import _ from 'lodash';
import every from 'lodash/every';
import some from 'lodash/some';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';

import { Resource } from 'src/actions/shared/createResource';
import { RootState } from 'src/actions/configuration/rootReducer';

interface Options {
  fetch?: boolean;
  params?: any;
  query?: any;
  body?: unknown;
  headers?: Record<string, string>;
}

export const STATUS_SUCCEEDED = 'succeeded';
export const STATUS_LOADING = 'loading';
export const STATUS_FAILED = 'failed';
export const STATUS_UNSTARTED = 'unstarted';
export const STATUS_PERSISTING = 'persisting';

export type ResourceStatus =
  | typeof STATUS_SUCCEEDED
  | typeof STATUS_LOADING
  | typeof STATUS_FAILED
  | typeof STATUS_UNSTARTED;

export const isLoadingStatus = (status: string): boolean => status === STATUS_LOADING || status === STATUS_UNSTARTED;

export const allFinished = (...states: string[]): boolean => {
  return every(states, state => state === STATUS_SUCCEEDED || state === STATUS_FAILED);
};

export const allSucceeded = (...states: string[]): boolean => {
  return every(states, state => state === STATUS_SUCCEEDED);
};

export const someFailed = (...states: string[]): boolean => {
  return some(states, state => state === STATUS_FAILED);
};

export const getConsolidatedState = (states: string[]): ResourceStatus => {
  return allSucceeded(...states) ? STATUS_SUCCEEDED : STATUS_LOADING;
};

export default function useResource<T>(
  resource: Resource<T>,
  options: Options = { fetch: true, params: null, query: null },
  unloadOnUnmount = true
) {
  const dispatch = useDispatch();
  const optionsHash = JSON.stringify(options);

  useEffect(
    () => {
      if (options.fetch) {
        const clonedOptions = _.cloneDeep(options);
        dispatch(
          resource.effects.fetchAll(clonedOptions.params, clonedOptions.query, {
            body: options.body,
            headers: options.headers,
          })
        );

        return () => {
          if (unloadOnUnmount) {
            dispatch(resource.effects.unload(clonedOptions.params));
          }
        };
      }
    },
    [dispatch, resource, optionsHash, unloadOnUnmount] // eslint-disable-line
  );
  return useResourceValues(resource);
}

export function useResourceAll<T>(resource: Resource<T>) {
  const resourceValue = useResourceValues(resource);

  const dispatch = useDispatch();

  const { loadingState } = resourceValue;

  useEffect(() => {
    if (loadingState === STATUS_UNSTARTED) {
      dispatch(resource.effects.fetchAll());
    }
  }, [loadingState, dispatch, resource]);

  return resourceValue;
}

export function useResourceValues<T>(resource: Resource<T>) {
  const state = useSelector((state: RootState) => state[resource.name]);
  return {
    ...state,
    values: useMemo(() => _.cloneDeep(state.values), [state.values]),
  };
}
